import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Can from '../../components/Can';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tooltip,
  Grid,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  Paid as PaidIcon
} from '@mui/icons-material';
import ReplyIcon from '@mui/icons-material/Reply';
import { useSnackbar } from 'notistack';
import facturaCompraService, { FacturaCompra, FacturaCompraSearchParams } from '../../services/facturaCompraService';
import { Proveedor } from '../../services/proveedorService';
import ProveedorSearch from '../../components/ProveedorSearch';
import { formatCurrency, formatDateLocal, toLocalDateStringYMD, formatProveedorConNro } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return toLocalDateStringYMD(d);
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return toLocalDateStringYMD(d);
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const s = formatDateLocal(fecha);
  return s || '-';
};

/** Formato Comprobante: A-0001-00000001 */
const formatoComprobante = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

function tipoToPrefijo(tipo?: string): string {
  const t = (tipo || '').trim();
  const map: Record<string, string> = {
    '1': 'FC',
    '2': 'ND',
    '3': 'NC',
  };
  return map[t] || t || 'FC';
}

/** FC (1) o ND compra (2) pueden originar una NC de devolución. */
const puedeDevolverNc = (fc: FacturaCompra): boolean => {
  const tipo = String(fc.nroTipoFormulario ?? '1').trim();
  return (tipo === '1' || tipo === '2') && !esFacturaAnulada(fc);
};

/** Anulada solo cuando el backend marcó explícitamente Nombre = ANULADO (Asentado=A en FC legacy no implica anulación). */
const esFacturaAnulada = (fc: FacturaCompra): boolean =>
  (fc.nombre ?? '').trim().toUpperCase() === 'ANULADO';

const puedePagarFactura = (fc: FacturaCompra): boolean =>
  String(fc.nroTipoFormulario ?? '1').trim() === '1' &&
  !esFacturaAnulada(fc) &&
  Number(fc.nroProveedor) > 0;

const FacturasCompra: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [facturas, setFacturas] = useState<FacturaCompra[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; facturaId: number } | null>(null);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [filters, setFilters] = useState(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    return {
      searchTerm: '',
      fechaDesde: (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) ? desde : getFirstDayOfMonth(),
      fechaHasta: (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) ? hasta : getLastDayOfMonth(),
      nroProveedor: '',
      letra: '',
      nroSuc: '',
      nroComprobante: ''
    };
  });

  const fetchFacturas = useCallback(async (
    pageNum: number = page,
    pageSize: number = rowsPerPage,
    filtros: typeof filters = filters
  ) => {
    try {
      setLoading(true);
      const params: FacturaCompraSearchParams = {
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: filtros.searchTerm || undefined,
        fechaDesde: filtros.fechaDesde || undefined,
        fechaHasta: filtros.fechaHasta || undefined,
        nroProveedor: filtros.nroProveedor || undefined,
        letra: filtros.letra || undefined,
        nroSuc: filtros.nroSuc ? parseInt(filtros.nroSuc, 10) : undefined,
        nroComprobante: filtros.nroComprobante ? parseInt(filtros.nroComprobante, 10) : undefined
      };
      const result = await facturaCompraService.getFacturasCompra(params);
      setFacturas(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: unknown) {
      console.error('Error al cargar facturas de compra:', error);
      const msg = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : 'Error al cargar facturas de compra';
      enqueueSnackbar(msg || 'Error al cargar facturas de compra', { variant: 'error' });
      setFacturas([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchFacturas();
  }, []);

  // Sincronizar fechas desde URL cuando cambian los searchParams (ej. al llegar desde dashboard)
  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    setFilters(f => ({
      ...f,
      ...(desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? { fechaDesde: desde } : {}),
      ...(hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? { fechaHasta: hasta } : {})
    }));
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchFacturas(0, rowsPerPage, filters);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchFacturas(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchFacturas(0, newRowsPerPage, filters);
  };

  const handleBuscar = () => {
    setPage(0);
    fetchFacturas(0, rowsPerPage, filters);
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setFilters((f) => ({ ...f, nroProveedor: String(proveedor.nroProveedor) }));
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
  };

  const handleComprobanteClick = (nroFormulario: number) => {
    if (nroFormulario > 0) navigate(`/compras/facturas/${nroFormulario}`);
  };

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    textTransform: 'none',
    minWidth: 0,
    p: 0,
    '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, facturaId: number) => {
    setKebabAnchor({ el: event.currentTarget, facturaId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const currentKebabFactura = kebabAnchor
    ? facturas.find((f) => f.nroFormulario === kebabAnchor.facturaId)
    : null;

  const handleAnular = async (facturaId: number) => {
    if (anulandoId != null) return;
    try {
      const check = await facturaCompraService.puedeAnularFacturaCompra(facturaId);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular esta factura de compra', { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular esta factura de compra?')) return;

      setAnulandoId(facturaId);
      await facturaCompraService.anularFacturaCompra(facturaId);
      enqueueSnackbar('Factura de compra anulada correctamente', { variant: 'success' });
      await fetchFacturas(page, rowsPerPage, filters);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la factura de compra'), { variant: 'error' });
    } finally {
      setAnulandoId(null);
    }
  };

  const renderAccionesFactura = (fc: FacturaCompra) => (
    <>
      <Tooltip title="Ver">
        <IconButton
          size="small"
          onClick={() => navigate(`/compras/facturas/${fc.nroFormulario}`)}
          sx={{ color: '#1976d2' }}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      {puedeDevolverNc(fc) && (
        <Tooltip title="Devolver (Nota de crédito)">
          <IconButton
            size="small"
            onClick={() => navigate(`/compras/notas-credito/nuevo?devolverDesde=${fc.nroFormulario}`)}
            sx={{
              bgcolor: '#ff9800',
              color: '#fff',
              borderRadius: 1.5,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: '#f57c00', transform: 'scale(1.1)' },
              transition: 'all 0.2s'
            }}
          >
            <ReplyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!esFacturaAnulada(fc) && (
        <Tooltip title="Más acciones">
          <IconButton
            size="small"
            onClick={(e) => handleKebabOpen(e, fc.nroFormulario)}
            sx={{ color: '#1976d2' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const renderKebabItems = (fc: FacturaCompra) => {
    if (esFacturaAnulada(fc)) return null;

    return [
      ...(puedePagarFactura(fc)
        ? [
            <MenuItem
              key="pagar"
              onClick={() => {
                handleKebabClose();
                navigate(
                  `/tesoreria/orden-pago/nuevo?nroProveedor=${fc.nroProveedor}&nroFormularioCompra=${fc.nroFormulario}`
                );
              }}
            >
              <ListItemIcon><PaidIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>Pagar</ListItemText>
            </MenuItem>,
          ]
        : []),
      <MenuItem
        key="anular"
        onClick={() => {
          handleKebabClose();
          void handleAnular(fc.nroFormulario);
        }}
        disabled={anulandoId === fc.nroFormulario}
        sx={{ color: 'error.main' }}
      >
        <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
        <ListItemText>Anular factura</ListItemText>
      </MenuItem>,
    ];
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Facturas de Compra
            </Typography>
            <Can permiso="compras.facturas.nuevo">
            <Button
              variant="contained"
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              startIcon={<AddIcon />}
              onClick={() => navigate('/compras/facturas/nuevo')}
            >
              Nueva Factura
            </Button>
            </Can>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por número, comprobante o nombre..."
                value={filters.searchTerm}
                onChange={(e) => setFilters(f => ({ ...f, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha desde"
                value={filters.fechaDesde}
                onChange={(e) => setFilters(f => ({ ...f, fechaDesde: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha hasta"
                value={filters.fechaHasta}
                onChange={(e) => setFilters(f => ({ ...f, fechaHasta: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Letra"
                value={filters.letra}
                onChange={(e) => setFilters(f => ({ ...f, letra: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Suc"
                value={filters.nroSuc}
                onChange={(e) => setFilters(f => ({ ...f, nroSuc: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Nro. Comp."
                value={filters.nroComprobante}
                onChange={(e) => setFilters(f => ({ ...f, nroComprobante: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Nro. proveedor"
                placeholder="Ej: 1001"
                value={filters.nroProveedor}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setFilters((f) => ({ ...f, nroProveedor: v }));
                  setNombreProveedorFiltro('');
                }}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar proveedor por nombre">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setOpenProveedorSearch(true)}
                          aria-label="Buscar proveedor por nombre"
                          sx={{ color: '#1976d2' }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText={nombreProveedorFiltro ? `Nombre: ${nombreProveedorFiltro}` : undefined}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="outlined" onClick={handleBuscar} sx={{ borderRadius: 2, color: '#1976d2', borderColor: '#1976d2' }}>
                Buscar
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {facturas.map((fc) => (
                  <Card
                    key={fc.nroFormulario}
                    variant="outlined"
                    sx={{ p: 2, opacity: esFacturaAnulada(fc) ? 0.65 : 1 }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Button
                          variant="text"
                          onClick={() => handleComprobanteClick(fc.nroFormulario)}
                          sx={comprobanteSx}
                        >
                          [{tipoToPrefijo(fc.nroTipoFormulario)}] {formatoComprobante(fc.letra, fc.nroSuc, fc.nroComprobante ?? undefined)}
                        </Button>
                        {esFacturaAnulada(fc) && (
                          <Chip label="Anulado" size="small" color="default" variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Fecha: {formatearFecha(fc.fechaEmision)} | Proveedor: {formatProveedorConNro(fc.nroProveedor, fc.nombre)} | Total: {formatCurrency(fc.importeTotal ?? 0)}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {renderAccionesFactura(fc)}
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturas.map((fc) => (
                    <TableRow
                      key={fc.nroFormulario}
                      hover
                      sx={{ opacity: esFacturaAnulada(fc) ? 0.65 : 1 }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{formatearFecha(fc.fechaEmision)}</span>
                          {esFacturaAnulada(fc) && (
                            <Chip label="Anulado" size="small" color="default" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                        <Button
                          variant="text"
                          onClick={() => handleComprobanteClick(fc.nroFormulario)}
                          sx={comprobanteSx}
                        >
                          {formatoComprobante(fc.letra, fc.nroSuc, fc.nroComprobante ?? undefined)}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{formatProveedorConNro(fc.nroProveedor, fc.nombre)}</TableCell>
                      <TableCell>{tipoToPrefijo(fc.nroTipoFormulario)}</TableCell>
                      <TableCell align="right">{formatCurrency(fc.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">
                        {renderAccionesFactura(fc)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      <Menu
        anchorEl={kebabAnchor?.el ?? null}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
      >
        {currentKebabFactura ? renderKebabItems(currentKebabFactura) : null}
      </Menu>
    </Box>
  );
};

export default FacturasCompra;
